import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import client from '../api/client';
import Layout from '../components/Layout';
import './Dashboard.css';

const QueueIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <rect x="2" y="4" width="16" height="2.5" rx="1.25" fill="currentColor" opacity="0.9"/>
    <rect x="2" y="8.75" width="12" height="2.5" rx="1.25" fill="currentColor" opacity="0.6"/>
    <rect x="2" y="13.5" width="9" height="2.5" rx="1.25" fill="currentColor" opacity="0.35"/>
  </svg>
);

const FloorAuditIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <rect x="2" y="2" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.5"/>
    <rect x="11" y="2" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.5"/>
    <rect x="2" y="11" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M13.5 14.5l1.5 1.5 2.5-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const KioskIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <rect x="4" y="2" width="12" height="14" rx="2" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M7 18h6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M10 15v3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <circle cx="10" cy="8" r="2.5" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M7 13c0-1.657 1.343-2.5 3-2.5s3 .843 3 2.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

const TeamIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <circle cx="7.5" cy="7" r="2.5" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M3 16c0-2.209 2.015-4 4.5-4s4.5 1.791 4.5 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <circle cx="14" cy="6.5" r="2" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M17 15c0-1.657-1.343-3-3-3a2.99 2.99 0 00-1.5.4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

const InventoryIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path d="M3 7l7-4 7 4v9l-7 4-7-4V7z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
    <path d="M10 3v13M3 7l7 4 7-4" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
  </svg>
);

const DepartmentsIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <rect x="2" y="2" width="7" height="5" rx="1.5" stroke="currentColor" strokeWidth="1.5"/>
    <rect x="2" y="13" width="7" height="5" rx="1.5" stroke="currentColor" strokeWidth="1.5"/>
    <rect x="11" y="7.5" width="7" height="5" rx="1.5" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M9 4.5h1.5a1 1 0 011 1v2M9 15.5h1.5a1 1 0 001-1v-2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

const BillingIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <rect x="2" y="5" width="16" height="12" rx="2" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M2 9h16" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M6 13h3M13 13h1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

const JobsIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <rect x="4" y="2" width="12" height="16" rx="2" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M7 7h6M7 10.5h6M7 14h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

const QUICK_ACTIONS = [
  { label: 'Queue',          sub: 'View live job queue',          path: '/queue',               icon: QueueIcon,       color: '#3b82f6' },
  { label: 'Floor Audit',    sub: 'Start or resume an audit',     path: '/floor-audit',         icon: FloorAuditIcon,  color: '#22c55e' },
  { label: 'Kiosk Settings', sub: 'Manage operators and PINs',    path: '/settings/kiosk',      icon: KioskIcon,       color: '#8b5cf6' },
  { label: 'Team',           sub: 'Manage users and roles',       path: '/settings/team',       icon: TeamIcon,        color: '#f59e0b' },
  { label: 'Inventory',      sub: 'Track stock and scan items',   path: '/inventory',           icon: InventoryIcon,   color: '#14b8a6' },
  { label: 'Departments',    sub: 'Organize your floor',          path: '/departments',         icon: DepartmentsIcon, color: '#f97316' },
  { label: 'Jobs',           sub: 'View and manage all jobs',     path: '/jobs',                icon: JobsIcon,        color: '#6b7280' },
  { label: 'Billing',        sub: 'Manage your plan',             path: '/settings/billing',    icon: BillingIcon,     color: '#ec4899' },
];

export default function Dashboard() {
  const { org }                       = useAuth();
  const navigate                      = useNavigate();
  const [summary, setSummary]         = useState(null);
  const [loading, setLoading]         = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await client.get('/reports/summary');
        setSummary(res.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) return <Layout><div style={{color:'#6b7280'}}>Loading...</div></Layout>;

  return (
    <Layout>
      <div className="dash-header">
        <h1>Dashboard</h1>
        <div className="dash-plan">{org?.plan?.toUpperCase()} PLAN</div>
      </div>

      {/* Stats */}
      <div className="dash-stats">
        <div className="stat">
          <div className="stat-label">Total Jobs</div>
          <div className="stat-value">
            {summary?.jobs?.reduce((sum, j) => sum + parseInt(j.count), 0) || 0}
          </div>
          <div className="stat-sub">
            {summary?.jobs?.map(j => `${j.count} ${j.status}`).join(' · ')}
          </div>
        </div>
        <div className="stat">
          <div className="stat-label">Inventory Items</div>
          <div className="stat-value">{summary?.inventory?.total_items || 0}</div>
          <div className="stat-sub">{summary?.inventory?.total_quantity || 0} units total</div>
        </div>
        <div className="stat">
          <div className="stat-label">Active Locations</div>
          <div className="stat-value">{summary?.locations?.total || 0}</div>
          <div className="stat-sub">tracked across the floor</div>
        </div>
        <div className="stat">
          <div className="stat-label">Never Scanned</div>
          <div className="stat-value amber">{summary?.inventory?.never_scanned || 0}</div>
          <div className="stat-sub">missing confirmation</div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="dash-section-label">Quick Actions</div>
      <div className="dash-quick-grid">
        {QUICK_ACTIONS.map(({ label, sub, path, icon: Icon, color }) => (
          <div
            key={path}
            className="dash-quick-card"
            onClick={() => navigate(path)}
            style={{ '--card-color': color }}
          >
            <div className="dash-quick-icon" style={{ color }}>
              <Icon />
            </div>
            <div className="dash-quick-label">{label}</div>
            <div className="dash-quick-sub">{sub}</div>
          </div>
        ))}
      </div>

      {/* Recent Activity */}
      <div className="dash-section-label" style={{marginTop:'40px'}}>Recent Activity</div>
      <div className="dash-activity">
        {(!summary?.recent_activity || summary.recent_activity.length === 0) && (
          <div className="dash-empty">No activity yet</div>
        )}
        {summary?.recent_activity?.map((a, i) => (
          <div key={i} className="dash-activity-row">
            <div className="dash-activity-action">{a.action}</div>
            <div className="dash-activity-meta">
              {a.user_email} · {new Date(a.created_at).toLocaleString()}
            </div>
          </div>
        ))}
      </div>
    </Layout>
  );
}